import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-full text-base font-semibold tracking-wider uppercase whitespace-normal text-center break-words leading-tight no-underline transition-[color,background-color,border-color,box-shadow,opacity] outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  {
    variants: {
      variant: {
        // Primary CTA — system accent (orange)
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20',
        // Neutral outline — dismiss / Cancel (not a CTA)
        outline:
          'border border-input bg-background hover:bg-interaction-neutral hover:text-foreground focus-visible:bg-interaction-neutral focus-visible:text-foreground',
        // Secondary CTA — accent outline (Explore, Learn more); softer than filled primary
        secondary:
          'border-primary text-primary hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground border bg-card',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        // Link — system accent (same as --link)
        link: 'text-link link-text rounded-none',
      },
      size: {
        default: 'min-h-10 px-6 py-2 has-[>svg]:px-5',
        xs: "min-h-7 gap-1 px-3 py-1 text-sm has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3",
        sm: 'min-h-9 px-4 py-1.5 text-sm has-[>svg]:px-3',
        lg: 'min-h-11 px-8 py-2.5 has-[>svg]:px-6',
        icon: 'size-10',
        'icon-xs': "size-7 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-9',
        'icon-lg': 'size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
